class CartService {
    _getCartForm() {
        return document.querySelector("#kd-cart-form");
    }

    getCartUrl() {
        const form = this._getCartForm();
        return form?.dataset.cartUrl || window.location.href;
    }

    getCurrentLanguage() {
        return (
            window?.languageSettings?.current_language ||
            document.documentElement.lang?.split("-")[0] ||
            "de"
        );
    }

    getCurrentCurrency() {
        return (
            window?.wcml_mc_settings?.current_currency?.code ||
            window?.woocs_current_currency?.name ||
            ""
        );
    }

    getAjaxUrl(endpoint) {
        const url = new URL(window.location.href);
        url.hash = "";
        url.search = "";
        url.searchParams.set("wc-ajax", endpoint);
        url.searchParams.set("lang", this.getCurrentLanguage());

        return url.toString();
    }

    appendRequestContext(formData) {
        formData.append("lang", this.getCurrentLanguage());

        const currency = this.getCurrentCurrency();
        if (currency) {
            formData.set("currency", currency);
            formData.set("woocommerce-currency-switcher", currency);
        }
    }

    async _requestCartUpdate(op, data = {}) {
        const formData = new FormData();
        formData.append("op", op);
        this.appendRequestContext(formData);

        if (data.itemKey) {
            formData.append("item_key", data.itemKey);
        }

        if (typeof data.qty === "number") {
            formData.append("qty", String(data.qty));
        }

        if (typeof data.amount === "number") {
            formData.append("amount", String(data.amount));
        }

        try {
            const response = await fetch(this.getAjaxUrl("kd_update_cart"), {
                method: "POST",
                body: formData,
                credentials: "same-origin",
                headers: { Accept: "application/json" },
            });

            if (!response.ok) {
                console.warn("kd_update_cart failed with status", response.status);
                return null;
            }

            const payload = await response.json();
            if (!payload || typeof payload !== "object") {
                console.warn("kd_update_cart returned invalid JSON", payload);
                return null;
            }

            return payload;
        } catch (error) {
            console.warn("kd_update_cart request failed", error);
            return null;
        }
    }

    _applyCartPayload(payload, options = {}) {
        if (!payload || typeof payload !== "object") return;

        if (payload.is_empty) {
            window.location.href = this.getCartUrl();
            return;
        }

        // Linke Produktliste komplett neu rendern
        if (payload.items_html) {
            const itemsBox = document.querySelector("#kd-cart-items");
            if (itemsBox) {
                itemsBox.innerHTML = payload.items_html;
            }
        } else if (options.removeItemKey) {
            // Fallback: nur gezielt entfernen, wenn keine items_html mitkommt
            const row = document.querySelector(`[data-cart-item-key="${options.removeItemKey}"]`);
            if (row) {
                row.remove();
            }
        }

        // Rechte Totals-Box aktualisieren
        const totalsBox = document.querySelector("#kd-cart-totals");
        if (totalsBox && payload.totals_html) {
            totalsBox.innerHTML = payload.totals_html;
            this.initShippingShow();
            this.initAjaxCoupons();
            this.initDonationBox();
        }

        window.dispatchEvent(new CustomEvent("kd:cart:update", { detail: payload }));
    }

    async applyCoupon(code) {
        const trimmed = String(code || "").trim();
        if (!trimmed) return;

        const formData = new FormData();
        formData.append("coupon_code", trimmed);
        formData.append("op", "add");
        this.appendRequestContext(formData);

        try {
            const response = await fetch(this.getAjaxUrl("kd_apply_coupon"), {
                method: "POST",
                body: formData,
                credentials: "same-origin",
                headers: { Accept: "application/json" },
            });

            if (!response.ok) {
                console.warn("kd_apply_coupon failed with status", response.status);
                return;
            }

            const payload = await response.json();
            if (!payload || typeof payload !== "object") {
                console.warn("kd_apply_coupon returned invalid JSON", payload);
                return;
            }

            this._applyCartPayload(payload);
        } catch (error) {
            console.warn("kd_apply_coupon request failed", error);
        }
    }

    async removeCoupon(code) {
        const trimmed = String(code || "").trim();
        if (!trimmed) return;

        const formData = new FormData();
        formData.append("coupon_code", trimmed);
        formData.append("op", "remove");
        this.appendRequestContext(formData);

        try {
            const response = await fetch(this.getAjaxUrl("kd_apply_coupon"), {
                method: "POST",
                body: formData,
                credentials: "same-origin",
                headers: { Accept: "application/json" },
            });

            if (!response.ok) {
                console.warn("kd_apply_coupon (remove) failed with status", response.status);
                return;
            }

            const payload = await response.json();
            if (!payload || typeof payload !== "object") {
                console.warn("kd_apply_coupon (remove) returned invalid JSON", payload);
                return;
            }

            this._applyCartPayload(payload);
        } catch (error) {
            console.warn("kd_apply_coupon (remove) request failed", error);
        }
    }

    initAjaxCoupons() {
        const input = document.querySelector("#coupon_code");
        const button = document.querySelector("#kd-apply-coupon");
        if (!input || !button) return;

        if (!button.dataset.kdBound) {
            const handleApply = (event) => {
                if (event) event.preventDefault();
                const code = input.value || "";
                this.applyCoupon(code);
            };

            button.addEventListener("click", handleApply);

            input.addEventListener("keydown", (event) => {
                if (event.key === "Enter") {
                    event.preventDefault();
                    handleApply(event);
                }
            });

            button.dataset.kdBound = "1";
        }

        if (!this._couponRemoveBound) {
            document.addEventListener(
                "click",
                (event) => {
                    const link = event.target.closest(".woocommerce-remove-coupon");
                    if (!link) return;

                    // Browser-Default UND WooCommerce-Handler verhindern
                    event.preventDefault();
                    event.stopImmediatePropagation();

                    let code = link.dataset.coupon || "";

                    if (!code && link.href) {
                        try {
                            const url = new URL(link.href, window.location.origin);
                            code = url.searchParams.get("remove_coupon") || "";
                        } catch {
                            // Intentionally silent; WooCommerce handles validation internally.
                        }
                    }

                    if (!code) {
                        console.warn("Could not determine coupon code for remove", link);
                        return;
                    }

                    this.removeCoupon(code);
                },
                true
            );

            this._couponRemoveBound = true;
        }
    }

    async updateCartItem(itemKey, qty) {
        if (!itemKey) return;

        if (qty <= 0) {
            return this.removeCartItem(itemKey);
        }

        const payload = await this._requestCartUpdate("qty", { itemKey, qty });
        this._applyCartPayload(payload);
    }

    async removeCartItem(itemKey) {
        if (!itemKey) return;

        const payload = await this._requestCartUpdate("remove", { itemKey });
        this._applyCartPayload(payload, { removeItemKey: itemKey });
    }

    initShippingShow() {
        const shippingShow = document.querySelector("#shipping-calculator-button");
        const shippingForm = document.querySelector("#shipping-calculator-form");
        if (!shippingShow || !shippingForm) return;

        shippingShow.addEventListener("click", (event) => {
            event.preventDefault();
            shippingForm.classList.remove("hidden");
            shippingShow.classList.add("hidden");
        });
    }

    initAjaxAddToCart(formSelector = ".js-add-to-cart-form") {
        if (this._ajaxAddBound) {
            return;
        }
        this._ajaxAddBound = true;

        const showErrorNoticeHtml = (noticeHtml) => {
            if (!noticeHtml) {
                return;
            }

            window.dispatchEvent(
                new CustomEvent("kd:notice", {
                    detail: {
                        type: "error",
                        html: noticeHtml,
                    },
                })
            );
        };

        document.addEventListener(
            "submit",
            async (event) => {
                const form = event.target.closest(formSelector);
                if (!form) return;

                event.preventDefault();
                event.stopPropagation?.();
                event.stopImmediatePropagation?.();

                if (form.dataset.busy === "1") return;
                form.dataset.busy = "1";

                try {
                    const formData = new FormData(form);
                    this.appendRequestContext(formData);

                    const button = form.querySelector('[name="add-to-cart"]');

                    let productId = null;

                    if (button && button.value && button.value !== "0") {
                        productId = button.value;
                    } else if (form.dataset.productId && form.dataset.productId !== "0") {
                        productId = form.dataset.productId;
                    } else if (formData.get("product_id") && formData.get("product_id") !== "0") {
                        productId = formData.get("product_id");
                    }

                    if (productId) {
                        formData.set("product_id", String(productId));
                        formData.delete("add-to-cart");
                    }

                    if (!formData.get("quantity")) {
                        formData.set("quantity", "1");
                    }

                    const response = await fetch(this.getAjaxUrl("kd_add_to_cart"), {
                        method: "POST",
                        body: formData,
                        credentials: "same-origin",
                        headers: {
                            Accept: "application/json",
                        },
                    });

                    if (!response.ok) {
                        console.warn("kd_add_to_cart failed with status", response.status);
                        return;
                    }

                    let payload = null;

                    try {
                        payload = await response.json();
                    } catch (error) {
                        console.warn("kd_add_to_cart returned invalid JSON", error);
                        return;
                    }

                    if (!payload || typeof payload !== "object") {
                        return;
                    }

                    if (!payload.success) {
                        showErrorNoticeHtml(payload.notice_html || "");
                        return;
                    }

                    this.openCartSidebar();

                    if (payload.cart && typeof payload.cart === "object") {
                        window.dispatchEvent(
                            new CustomEvent("kd:cart:update", { detail: payload.cart })
                        );

                        if (payload.cart.notice?.text) {
                            window.dispatchEvent(
                                new CustomEvent("kd:cart:notify", {
                                    detail: payload.cart.notice,
                                })
                            );
                        }
                    }
                } catch (error) {
                    console.warn("Add to cart failed:", error);
                } finally {
                    delete form.dataset.busy;
                }
            },
            { capture: true }
        );
    }

    openCartSidebar() {
        window.dispatchEvent(new CustomEvent("kd:cart:open"));
    }

    initDonationBox() {
        const input = document.querySelector('input[name="kd_donation_amount"]');
        const button = document.querySelector("[data-kd-donation-button]");

        if (!input || !button) return;
        if (button.dataset.kdBound === "1") return;

        const handleClick = (event) => {
            if (event) event.preventDefault();
            const raw = input.value || "";
            const value = parseFloat(raw.replace(",", "."));
            if (Number.isNaN(value) || value < 0) return;
            this.updateDonation(value);
        };

        button.addEventListener("click", handleClick);
        button.dataset.kdBound = "1";
    }

    async updateDonation(amount) {
        const normalized =
            typeof amount === "number" ? amount : parseFloat(String(amount).replace(",", "."));

        if (Number.isNaN(normalized) || normalized < 0) {
            return;
        }

        const payload = await this._requestCartUpdate("donation", { amount: normalized });
        this._applyCartPayload(payload);
    }

    initCartQuantityControls() {
        if (this._cartQuantityControlsBound) {
            return;
        }
        this._cartQuantityControlsBound = true;

        document.addEventListener("click", (event) => {
            const target = event.target;

            const minusButton = target.closest?.("[data-kd-qty-minus]");
            const plusButton = target.closest?.("[data-kd-qty-plus]");
            const removeButton = target.closest?.("[data-kd-remove-item]");

            // Entfernen
            if (removeButton) {
                event.preventDefault();
                const cartItemKey = removeButton.dataset.itemKey || "";
                if (!cartItemKey) return;

                this.removeCartItem(cartItemKey);
                return;
            }

            // Plus / Minus
            if (!minusButton && !plusButton) {
                return;
            }

            event.preventDefault();

            const button = minusButton || plusButton;
            const cartItemKey = button.dataset.itemKey || "";
            if (!cartItemKey) return;

            const quantityInput = document.querySelector(`input[data-item-key="${cartItemKey}"]`);
            if (!quantityInput) return;

            let currentQuantity = parseInt(quantityInput.value, 10);
            if (Number.isNaN(currentQuantity)) {
                currentQuantity = 0;
            }

            let maxQuantity = parseInt(quantityInput.dataset.maxQty, 10);
            if (Number.isNaN(maxQuantity) || maxQuantity < 0) {
                maxQuantity = 0;
            }

            const change = minusButton ? -1 : 1;
            let newQuantity = currentQuantity + change;

            if (newQuantity < 0) {
                newQuantity = 0;
            }

            if (plusButton && maxQuantity > 0 && newQuantity > maxQuantity) {
                newQuantity = maxQuantity;
            }

            if (newQuantity === currentQuantity) {
                return;
            }

            quantityInput.value = String(newQuantity);
            this.updateCartItem(cartItemKey, newQuantity);
        });
    }
}

export default CartService;
